import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';

/** Тохиргоо → Платформ: бүх (эсвэл сонгосон) СӨХ-ийн оршин суугчдад масс SMS. Кирилл 70 / латин 160 тэмдэгт = 1 сегмент. */
type T = { id: string; name: string; units_with_phone: number };
type Summary = { recipients: number; segments: number; total_sms: number; tenants: { name: string; count: number }[]; sample: string; sent?: number; failed?: number; error?: string };

const isLatin = (t: string) => /^[\x00-\x7F]*$/.test(t);
const limit = (t: string) => (isLatin(t) ? 160 : 70);
const segs = (t: string) => Math.max(1, Math.ceil((t || ' ').length / limit(t)));

export default function MassSmsCard() {
  const [cfg, setCfg] = useState<{ enabled: boolean; sender: string; items: T[] } | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());     // хоосон = бүгд
  const [text, setText] = useState('');
  const [sum, setSum] = useState<Summary | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { api.get<any>('/platform/sms/tenants').then(setCfg).catch(() => setCfg({ enabled: false, sender: '', items: [] })); }, []);
  const tenant_ids = useMemo(() => Array.from(sel), [sel]);
  useEffect(() => {
    const t = setTimeout(() => { api.post<Summary>('/platform/sms/mass', { text, tenant_ids, dry_run: true }).then(setSum).catch(() => setSum(null)); }, 350);
    return () => clearTimeout(t);
  }, [text, tenant_ids]);

  const toggle = (id: string) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const send = async () => {
    setConfirm(false); setBusy(true); setMsg('');
    try {
      const r: Summary = await api.post('/platform/sms/mass', { text, tenant_ids, dry_run: false });
      setMsg(`${r.sent} дугаарт илгээлээ${r.failed ? ` · ${r.failed} алдаатай${r.error ? ` (${r.error})` : ''}` : ''}`);
      setText('');
    } catch (e: any) { let d = 'Илгээж чадсангүй'; try { d = JSON.parse(e?.body || '{}')?.detail || d; } catch {} setMsg('Алдаа: ' + d); }
    finally { setBusy(false); }
  };

  const lim = limit(text); const n = text.length; const over = n > lim; const nseg = segs(text);
  const totalPhones = (cfg?.items || []).reduce((a, t) => a + t.units_with_phone, 0);

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginTop: 16 }}>
      <h3 style={{ margin: '0 0 4px', fontFamily: "'Inter'", fontWeight: 700, color: '#1c2b22' }}>Масс мессеж илгээх (SMS)</h3>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#5f6f66' }}>
        Бүх СӨХ-ийн утасны дугаартай айлуудад нэг мессеж илгээнэ. Хувьсагч: <code>{'{сөх}'}</code> (тухайн СӨХ-ийн нэр), <code>{'{айл}'}</code>, <code>{'{линк}'}</code> (айлын QR хуудас).
        {cfg?.enabled ? <> Илгээгч дугаар: <b>{cfg.sender}</b>.</> : <span style={{ color: '#b3412f' }}> SMS тохируулаагүй (CALLPRO_SMS_API_KEY).</span>}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)', gap: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12.5, color: '#5f6f66', fontWeight: 600, marginBottom: 4 }}>Мессеж</label>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={5} maxLength={500}
            placeholder="{сөх}: Сайн Хөрш системийн шинэчлэл… Дэлгэрэнгүй: {линк}"
            style={{ width: '100%', padding: '10px 12px', border: `1.5px solid ${over ? '#e0a090' : '#dcdacf'}`, borderRadius: 10, fontSize: 14, boxSizing: 'border-box', background: '#fbfaf6', resize: 'vertical', fontFamily: 'inherit' }} />
          {/* Кирилл / латин хязгаар */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, fontSize: 12.5 }}>
            <span style={{ padding: '4px 10px', borderRadius: 999, background: isLatin(text) ? '#eef1ee' : '#e3efe3', color: isLatin(text) ? '#5f6f66' : '#2f7a4a', fontWeight: 600 }}>
              Кирилл: 70 тэмдэгт / сегмент{!isLatin(text) ? ` · одоо ${n}/${70 * nseg}` : ''}
            </span>
            <span style={{ padding: '4px 10px', borderRadius: 999, background: isLatin(text) ? '#e3efe3' : '#eef1ee', color: isLatin(text) ? '#2f7a4a' : '#5f6f66', fontWeight: 600 }}>
              Латин: 160 тэмдэгт / сегмент{isLatin(text) ? ` · одоо ${n}/${160 * nseg}` : ''}
            </span>
            <span style={{ padding: '4px 10px', borderRadius: 999, background: nseg > 1 ? '#fff4e0' : '#eef1ee', color: nseg > 1 ? '#8a6d1d' : '#5f6f66', fontWeight: 700 }}>
              {n} тэмдэгт = {nseg} сегмент{nseg > 1 ? ' (дугаар тус бүрт ' + nseg + ' SMS зарцуулна)' : ''}
            </span>
          </div>
          <div style={{ fontSize: 12, color: '#8a978f', marginTop: 4 }}>Нэг кирилл үсэг ч орсон бол бүх мессеж кирилл (70) хязгаараар тоологдоно. Кирилл мессежийг 70-д багтаавал айл бүрт 1 SMS.</div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12.5, color: '#5f6f66', fontWeight: 600, marginBottom: 4 }}>Хүрээ — {sel.size === 0 ? `бүх СӨХ (${totalPhones} утастай айл)` : `${sel.size} СӨХ сонгосон`}</label>
          <div style={{ maxHeight: 190, overflow: 'auto', border: '1px solid #e8e5dc', borderRadius: 10, padding: '6px 10px', background: '#fbfaf6' }}>
            {(cfg?.items || []).map(t => (
              <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13.5, cursor: 'pointer', opacity: t.units_with_phone ? 1 : .5 }}>
                <input type="checkbox" checked={sel.has(t.id)} onChange={() => toggle(t.id)} disabled={!t.units_with_phone} />
                <span style={{ flex: 1 }}>{t.name}</span><span style={{ color: '#5f6f66', fontVariantNumeric: 'tabular-nums' }}>{t.units_with_phone}</span>
              </label>
            ))}
          </div>
          {sum && (
            <div style={{ marginTop: 10, background: '#f3f1ea', borderRadius: 10, padding: '10px 12px', fontSize: 13.5, lineHeight: 1.6 }}>
              Хүлээн авагч <b>{sum.recipients}</b> дугаар · <b>{sum.segments}</b> сегмент → нийт <b>{sum.total_sms}</b> SMS
              {sum.sample && <div style={{ marginTop: 6, fontSize: 12.5, color: '#2a3b32', whiteSpace: 'pre-wrap', borderTop: '1px dashed #dcdacf', paddingTop: 6 }}>{sum.sample}</div>}
            </div>
          )}
        </div>
      </div>

      {msg && <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, background: msg.startsWith('Алдаа') ? '#fee2e2' : '#f0fdf4', color: msg.startsWith('Алдаа') ? '#dc2626' : '#166534', fontSize: 13.5 }}>{msg}</div>}
      <button onClick={() => setConfirm(true)} disabled={!cfg?.enabled || !text.trim() || !sum?.recipients || busy}
        style={{ marginTop: 14, background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none', padding: '10px 22px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: (!cfg?.enabled || !text.trim() || !sum?.recipients || busy) ? .55 : 1 }}>
        {busy ? 'Илгээж байна…' : `📨 ${sum?.recipients || 0} дугаарт илгээх`}
      </button>

      {confirm && sum && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setConfirm(false)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 22, width: 460, maxWidth: '100%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Масс мессеж илгээх үү?</h3>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              <b>{sum.tenants.length}</b> СӨХ-ийн <b>{sum.recipients}</b> дугаарт, тус бүр <b>{sum.segments}</b> сегмент — нийт <b>{sum.total_sms}</b> SMS зарцуулна. Буцаах боломжгүй.
              <div style={{ maxHeight: 120, overflow: 'auto', fontSize: 12.5, color: '#5f6f66', margin: '8px 0' }}>{sum.tenants.map(t => <div key={t.name}>{t.name}: {t.count}</div>)}</div>
              <div style={{ background: '#f3f1ea', borderRadius: 8, padding: 10, whiteSpace: 'pre-wrap', fontSize: 13 }}>{sum.sample}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setConfirm(false)} style={{ background: '#e8e5dc', border: 'none', padding: '8px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 14 }}>Болих</button>
              <button onClick={send} style={{ background: '#236844', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Илгээх</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
